import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Bell,
  AlertTriangle,
  Info,
  CheckCircle,
  Clock,
  User,
  Plus,
  Edit,
  Trash2,
  X
} from 'lucide-react';
import { useQuery, useMutation } from '@/apollo';
import { gql } from '@/apollo';
import { useAuth } from '../../contexts/AuthContext';
import { TiptapEditor } from '../ui/TiptapEditor';
import { ConfirmModal } from '../../framework/modals/ConfirmModal';

// GraphQL Queries and Mutations
const GET_ACTIVE_BULLETINS = gql`
  query GetActiveBulletins {
    activeBulletins {
      id
      title
      content
      priority
      isActive
      expiresAt
      createdAt
      updatedAt
      author {
        id
        username
        firstName
        lastName
      }
    }
  }
`;

const CREATE_BULLETIN = gql`
  mutation CreateBulletin($input: CreateBulletinInput!) {
    createBulletin(input: $input) {
      id
      title
      content
      priority
      isActive
      expiresAt
      createdAt
      author {
        id
        username
        firstName
        lastName
      }
    }
  }
`;

const UPDATE_BULLETIN = gql`
  mutation UpdateBulletin($id: ID!, $input: UpdateBulletinInput!) {
    updateBulletin(id: $id, input: $input) {
      id
      title
      content
      priority
      isActive
      expiresAt
      updatedAt
      author {
        id
        username
        firstName
        lastName
      }
    }
  }
`;

const DELETE_BULLETIN = gql`
  mutation DeleteBulletin($id: ID!) {
    deleteBulletin(id: $id)
  }
`;

interface Bulletin {
  id: string;
  title: string;
  content: string;
  priority: 'LOW' | 'NORMAL' | 'HIGH' | 'URGENT';
  isActive: boolean;
  expiresAt?: string;
  createdAt: string;
  updatedAt: string;
  author: {
    id: string;
    username: string;
    firstName?: string;
    lastName?: string;
  };
}

interface BulletinsBoardProps {
  compact?: boolean;
  readOnly?: boolean;
}

export const BulletinsBoard: React.FC<BulletinsBoardProps> = ({ compact = false, readOnly = false }) => {
  const { user } = useAuth();
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [editingBulletin, setEditingBulletin] = useState<Bulletin | null>(null);
  const [deleteConfirmModal, setDeleteConfirmModal] = useState<{ isOpen: boolean; bulletinId: string | null }>({
    isOpen: false,
    bulletinId: null
  });
  const [formData, setFormData] = useState({
    title: '',
    content: '',
    priority: 'NORMAL' as 'LOW' | 'NORMAL' | 'HIGH' | 'URGENT',
    isActive: true,
    expiresAt: ''
  });

  // Fetch bulletins
  const { data, loading, error, refetch } = useQuery(GET_ACTIVE_BULLETINS, {
    fetchPolicy: 'network-only',
  });

  const bulletins = data?.activeBulletins || [];

  const [createBulletin, { loading: createLoading }] = useMutation(CREATE_BULLETIN, {
    onCompleted: () => {
      refetch();
      setShowCreateModal(false);
      resetForm();
    },
    onError: (error) => {
      console.error('Failed to create bulletin:', error);
      alert('Failed to create bulletin. Please try again.');
    }
  });

  const [updateBulletin, { loading: updateLoading }] = useMutation(UPDATE_BULLETIN, {
    onCompleted: () => {
      refetch();
      setShowCreateModal(false);
      setEditingBulletin(null);
      resetForm();
    },
    onError: (error) => {
      console.error('Failed to update bulletin:', error);
      alert('Failed to update bulletin. Please try again.');
    }
  });

  const [deleteBulletin] = useMutation(DELETE_BULLETIN, {
    onCompleted: () => {
      refetch();
    }
  });

  const resetForm = () => {
    setFormData({
      title: '',
      content: '',
      priority: 'NORMAL',
      isActive: true,
      expiresAt: ''
    });
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    // Validate form data
    if (!formData.title.trim()) {
      alert('Please enter a title for the bulletin');
      return;
    }
    
    if (!formData.content.trim()) {
      alert('Please enter content for the bulletin');
      return;
    }
    
    const input = {
      title: formData.title.trim(),
      content: formData.content.trim(),
      priority: formData.priority,
      isActive: formData.isActive,
      expiresAt: formData.expiresAt || null
    };

    if (editingBulletin) {
      updateBulletin({ variables: { id: editingBulletin.id, input } });
    } else {
      createBulletin({ variables: { input } });
    }
  };

  const handleEdit = (bulletin: Bulletin) => {
    setEditingBulletin(bulletin);
    setFormData({
      title: bulletin.title,
      content: bulletin.content,
      priority: bulletin.priority,
      isActive: bulletin.isActive,
      expiresAt: (bulletin.expiresAt ? new Date(bulletin.expiresAt).toISOString().split('T')[0] : '') as string
    });
    setShowCreateModal(true);
  };

  const handleDelete = (id: string) => {
    setDeleteConfirmModal({ isOpen: true, bulletinId: id });
  };

  const getPriorityIcon = (priority: string) => {
    switch (priority) {
      case 'URGENT': return <AlertTriangle className="w-4 h-4 text-red-600" />;
      case 'HIGH': return <AlertTriangle className="w-4 h-4 text-blue-600" />;
      case 'NORMAL': return <Info className="w-4 h-4 text-blue-600" />;
      case 'LOW': return <CheckCircle className="w-4 h-4 text-gray-600" />;
      default: return <Info className="w-4 h-4 text-gray-600" />;
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString();
  };

  const canManageBulletins = user && ['MODERATOR', 'ADMINISTRATOR'].includes(user.role);

  if (loading) {
    return (
      <div className="text-center py-8">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto mb-4"></div>
        <span className="text-gray-600">Loading bulletins...</span>
      </div>
    );
  }

  if (error) {
    return (
      <div className="text-center py-8">
        <AlertTriangle className="w-12 h-12 text-red-500 mx-auto mb-4" />
        <p className="text-red-600">Failed to load bulletins</p>
      </div>
    );
  }

  const displayBulletins = compact ? bulletins?.slice(0, 3) : bulletins;

  return (
    <div className="space-y-6">
      {/* Create Button - Only show for authorized users */}
      {canManageBulletins && !compact && !readOnly && (
        <div className="flex justify-end">
          <button
            onClick={() => {
              setEditingBulletin(null);
              resetForm();
              setShowCreateModal(true);
            }}
            className="flex items-center space-x-2 px-4 py-2 rounded-lg transition-colors"
            style={{
              backgroundColor: 'var(--color-accent)',
              color: 'white'
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.backgroundColor = 'var(--color-accent-hover)';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.backgroundColor = 'var(--color-accent)';
            }}
          >
            <Plus className="w-4 h-4" />
            <span>Create New Bulletin</span>
          </button>
        </div>
      )}

      {/* Bulletins List */}
      <div className="space-y-4">
        {displayBulletins?.length === 0 ? (
          <div 
            className="text-center py-12 rounded-lg"
            style={{
              backgroundColor: 'var(--color-bg-primary)',
              border: '1px solid var(--color-border)'
            }}
          >
            <Bell className="w-12 h-12 mx-auto mb-4" style={{ color: 'var(--color-text-muted)' }} />
            <h3 className="text-lg font-semibold mb-2" style={{ color: 'var(--color-text-primary)' }}>No bulletins</h3>
            <p style={{ color: 'var(--color-text-secondary)' }}>There are no active bulletins at the moment.</p>
          </div>
        ) : (
          displayBulletins?.map((bulletin: Bulletin) => (
            <motion.div
              key={bulletin.id}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="rounded-lg border p-4"
              style={{
                backgroundColor: 'var(--color-bg-primary)',
                borderColor: 'var(--color-border)'
              }}
            >
              <div className="flex items-start justify-between mb-3">
                <div className="flex items-center space-x-2">
                  {getPriorityIcon(bulletin.priority)}
                  <span className="text-sm font-medium uppercase" style={{ color: 'var(--color-text-secondary)' }}>
                    {bulletin.priority}
                  </span>
                </div>
                <h3 className="text-lg font-semibold" style={{ color: 'var(--color-text-primary)' }}>{bulletin.title}</h3>
              </div>
              
              <div 
                className="prose prose-sm max-w-none mb-4"
                style={{ color: 'var(--color-text-secondary)' }}
                dangerouslySetInnerHTML={{ __html: bulletin.content }}
              />
              
              <div className="flex items-center justify-between text-sm" style={{ color: 'var(--color-text-muted)' }}>
                <div className="flex items-center space-x-4">
                  <div className="flex items-center space-x-1">
                    <User className="w-3 h-3" />
                    <span>
                      {bulletin.author.firstName && bulletin.author.lastName
                        ? `${bulletin.author.firstName} ${bulletin.author.lastName}`
                        : bulletin.author.username}
                    </span>
                  </div>
                  <div className="flex items-center space-x-1">
                    <Clock className="w-3 h-3" />
                    <span>{formatDate(bulletin.createdAt)}</span>
                  </div>
                  {bulletin.expiresAt && (
                    <div className="flex items-center space-x-1">
                      <Clock className="w-3 h-3" />
                      <span>Expires: {formatDate(bulletin.expiresAt)}</span>
                    </div>
                  )}
                </div>
                
                {canManageBulletins && !compact && !readOnly && (
                  <div className="flex items-center space-x-2">
                    <button
                      onClick={() => handleEdit(bulletin)}
                      className="p-1 transition-colors"
                      style={{ color: 'var(--color-text-muted)' }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.color = 'var(--color-accent)';
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.color = 'var(--color-text-muted)';
                      }}
                      title="Edit bulletin"
                    >
                      <Edit className="w-4 h-4" />
                    </button>
                    <button
                      onClick={() => handleDelete(bulletin.id)}
                      className="p-1 transition-colors"
                      style={{ color: 'var(--color-text-muted)' }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.color = 'var(--color-error)';
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.color = 'var(--color-text-muted)';
                      }}
                      title="Delete bulletin"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                )}
              </div>
            </motion.div>
          ))
        )}
      </div>

      {/* Create/Edit Modal */}
      <AnimatePresence>
        {showCreateModal && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[100] p-4"
            onClick={(e) => {
              // Only close modal if clicking on the backdrop, not on modal content
              if (e.target === e.currentTarget) {
                setShowCreateModal(false);
              }
            }}
          >
            <motion.div
              initial={{ scale: 0.95, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.95, opacity: 0 }}
              className="rounded-lg shadow-xl w-full max-h-[80vh] overflow-y-auto dark-scrollbar"
              style={{
                backgroundColor: 'var(--color-bg-primary)',
                boxShadow: 'var(--shadow-xl)',
                maxWidth: '1200px' // Force a wider modal
              }}
              data-modal-content="true"
              onClick={(e) => e.stopPropagation()}
              onMouseDown={(e) => e.stopPropagation()}
              onMouseUp={(e) => e.stopPropagation()}
              onKeyDown={(e) => e.stopPropagation()}
              onPointerDown={(e) => e.stopPropagation()}
              onPointerUp={(e) => e.stopPropagation()}
            >
              <div className="p-6">
                <div className="flex items-center justify-between mb-6">
                  <h3 className="text-lg font-semibold" style={{ color: 'var(--color-text-primary)' }}>
                    {editingBulletin ? 'Edit Bulletin' : 'Create New Bulletin'}
                  </h3>
                  <button
                    onClick={() => setShowCreateModal(false)}
                    className="hover:opacity-70 transition-opacity"
                    style={{ color: 'var(--color-text-muted)' }}
                  >
                    <X className="w-6 h-6" />
                  </button>
                </div>

                <form 
                  onSubmit={handleSubmit} 
                  className="space-y-4"
                  onClick={(e) => e.stopPropagation()}
                  onMouseDown={(e) => e.stopPropagation()}
                  onMouseUp={(e) => e.stopPropagation()}
                  onKeyDown={(e) => e.stopPropagation()}
                >
                  <div>
                    <label className="block text-sm font-medium mb-2" style={{ color: 'var(--color-text-secondary)' }}>
                      Title *
                    </label>
                    <input
                      type="text"
                      value={formData.title}
                      onChange={(e) => setFormData(prev => ({ ...prev, title: e.target.value }))}
                      className="w-full px-3 py-2 border rounded-lg focus:ring-2 transition-all"
                      style={{
                        backgroundColor: 'var(--color-bg-secondary)',
                        borderColor: 'var(--color-border)',
                        color: 'var(--color-text-primary)',
                        '--tw-ring-color': 'var(--color-border-focus)'
                      } as any}
                      placeholder="Enter bulletin title..."
                      required
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium mb-2" style={{ color: 'var(--color-text-secondary)' }}>
                      Content *
                    </label>
                    <div 
                      onClick={(e) => e.stopPropagation()}
                      onMouseDown={(e) => e.stopPropagation()}
                      onMouseUp={(e) => e.stopPropagation()}
                      onKeyDown={(e) => e.stopPropagation()}
                    >
                      <TiptapEditor
                        key={editingBulletin ? `edit-${editingBulletin.id}` : 'create'}
                        content={formData.content}
                        onChange={(content) => {
                          setFormData(prev => ({ ...prev, content }));
                        }}
                        placeholder="Enter bulletin content..."
                        className="focus-within:ring-2 transition-all"
                      />
                    </div>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <div>
                      <label className="block text-sm font-medium mb-2" style={{ color: 'var(--color-text-secondary)' }}>
                        Priority
                      </label>
                      <select
                        value={formData.priority}
                        onChange={(e) => setFormData(prev => ({ ...prev, priority: e.target.value as any }))}
                        className="w-full px-3 py-2 border rounded-lg focus:ring-2 transition-all"
                        style={{
                          backgroundColor: 'var(--color-bg-secondary)',
                          borderColor: 'var(--color-border)',
                          color: 'var(--color-text-primary)',
                          '--tw-ring-color': 'var(--color-border-focus)'
                        } as any}
                      >
                        <option value="LOW">Low</option>
                        <option value="NORMAL">Normal</option>
                        <option value="HIGH">High</option>
                        <option value="URGENT">Urgent</option>
                      </select>
                    </div>

                    <div>
                      <label className="block text-sm font-medium mb-2" style={{ color: 'var(--color-text-secondary)' }}>
                        Status
                      </label>
                      <select
                        value={formData.isActive ? 'true' : 'false'}
                        onChange={(e) => setFormData(prev => ({ ...prev, isActive: e.target.value === 'true' }))}
                        className="w-full px-3 py-2 border rounded-lg focus:ring-2 transition-all"
                        style={{
                          backgroundColor: 'var(--color-bg-secondary)',
                          borderColor: 'var(--color-border)',
                          color: 'var(--color-text-primary)',
                          '--tw-ring-color': 'var(--color-border-focus)'
                        } as any}
                      >
                        <option value="true">Active</option>
                        <option value="false">Inactive</option>
                      </select>
                    </div>

                    <div>
                      <label className="block text-sm font-medium mb-2" style={{ color: 'var(--color-text-secondary)' }}>
                        Expires At
                      </label>
                      <input
                        type="date"
                        value={formData.expiresAt}
                        onChange={(e) => setFormData(prev => ({ ...prev, expiresAt: e.target.value }))}
                        className="w-full px-3 py-2 border rounded-lg focus:ring-2 transition-all"
                        style={{
                          backgroundColor: 'var(--color-bg-secondary)',
                          borderColor: 'var(--color-border)',
                          color: 'var(--color-text-primary)',
                          '--tw-ring-color': 'var(--color-border-focus)'
                        } as any}
                      />
                    </div>
                  </div>

                  <div className="flex justify-end space-x-3 pt-6 border-t" style={{ borderColor: 'var(--color-border)' }}>
                    <button
                      type="button"
                      onClick={() => setShowCreateModal(false)}
                      className="px-4 py-2 rounded-lg transition-colors"
                      style={{
                        backgroundColor: 'var(--color-bg-tertiary)',
                        color: 'var(--color-text-secondary)'
                      }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.backgroundColor = 'var(--color-bg-primary)';
                        e.currentTarget.style.color = 'var(--color-text-primary)';
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.backgroundColor = 'var(--color-bg-tertiary)';
                        e.currentTarget.style.color = 'var(--color-text-secondary)';
                      }}
                    >
                      Cancel
                    </button>
                    <button
                      type="submit"
                      disabled={!formData.title || !formData.content || createLoading || updateLoading}
                      className="px-4 py-2 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                      style={{
                        backgroundColor: 'var(--color-accent)',
                        color: 'white'
                      }}
                      onMouseEnter={(e) => {
                        if (!(!formData.title || !formData.content || createLoading || updateLoading)) {
                          e.currentTarget.style.backgroundColor = 'var(--color-accent-hover)';
                        }
                      }}
                      onMouseLeave={(e) => {
                        if (!(!formData.title || !formData.content || createLoading || updateLoading)) {
                          e.currentTarget.style.backgroundColor = 'var(--color-accent)';
                        }
                      }}
                    >
                      {(createLoading || updateLoading) ? (
                        <div className="flex items-center">
                          <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                          {editingBulletin ? 'Updating...' : 'Creating...'}
                        </div>
                      ) : (
                        `${editingBulletin ? 'Update' : 'Create'} Bulletin`
                      )}
                    </button>
                  </div>
                </form>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Delete Confirmation Modal */}
      <ConfirmModal
        isOpen={deleteConfirmModal.isOpen}
        onClose={() => setDeleteConfirmModal({ isOpen: false, bulletinId: null })}
        onConfirm={async () => {
          if (deleteConfirmModal.bulletinId) {
            await deleteBulletin({ variables: { id: deleteConfirmModal.bulletinId } });
            setDeleteConfirmModal({ isOpen: false, bulletinId: null });
          }
        }}
        title="Delete Bulletin"
        message="Are you sure you want to delete this bulletin? This action cannot be undone."
        type="danger"
        confirmLabel="Delete"
        cancelLabel="Cancel"
      />
    </div>
  );
};
